import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import axiosInstance from "../../../utils/axiosInstance";

const API_BASE = import.meta.env.VITE_API_BASE_URL;

function formatDate(iso) {
  if (!iso) return "-";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function getDisplayName(p) {
  return p?.fullName || p?.contactNo || `Patient #${p?.id ?? "-"}`;
}

function Badge({ children, className = "" }) {
  return (
    <span
      className={
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border " +
        className
      }>
      {children}
    </span>
  );
}

export default function Patients() {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchPatients = async () => {
    setLoading(true);
    setError("");

    try {
      const res = await axiosInstance.get(`${API_BASE}/api/patients`, {
        params: { limit: 5 },
        withCredentials: true,
      });

      setPatients(res.data?.patients || []);
    } catch (err) {
      setPatients([]);
      setError(err?.response?.data?.error || "Failed to load patients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPatients();
  }, []);

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">
              Recent Patients
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Latest registered patients 
            </p>
          </div>
          <Link
            to="/patient-management"
            className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200">
            View All Patients
          </Link>
        </div>
      </div>

      {/* Content */}
      <div className="">
        {loading ? (
          <div className="flex justify-center py-8">
            <div className="text-sm text-gray-600">Loading patients...</div>
          </div>
        ) : error ? (
          <div className="text-sm text-red-600 text-center py-8">{error}</div>
        ) : patients.length === 0 ? (
          <div className="text-center py-8 text-gray-600">
            <div className="text-gray-400 mb-2">No patients found</div>
            <p className="text-sm">
              Patients will appear here once registered.
            </p>
          </div>
        ) : (
          <>
            {/* Desktop table */}
            <div className="hidden lg:block overflow-hidden border border-gray-200 rounded-lg">
              <table className="w-full text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left font-medium px-4 py-3 text-gray-600 uppercase tracking-wider w-12">
                      SL.No
                    </th>
                    <th className="text-left font-medium px-4 py-3 text-gray-600 uppercase tracking-wider">
                      Patient Details
                    </th>
                    <th className="text-left font-medium px-4 py-3 text-gray-600 uppercase tracking-wider">
                      Contact
                    </th>

                    <th className="text-left font-medium px-4 py-3 text-gray-600 uppercase tracking-wider">
                      Reg. Date
                    </th>
                  </tr>
                </thead>

                <tbody className="divide-y divide-gray-200">
                  {patients.map((p, index) => (
                    <tr
                      key={p.id}
                      className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 text-gray-500 font-medium text-center">
                        {index + 1}
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <div className="flex-shrink-0">
                            <div className="w-7 h-7 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                              {getDisplayName(p).charAt(0).toUpperCase()}
                            </div>
                          </div>
                          <div className="min-w-0">
                            <div className="font-medium text-gray-900 truncate max-w-[160px]">
                              {getDisplayName(p)}
                            </div>
                            <div className="text-xs text-gray-500 truncate max-w-[160px]">
                              {p.email || "No email"}
                            </div>
                          </div>
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="text-gray-900 text-xs">
                          {p.contactNo || "-"}
                        </div>
                      </td>

                      <td className="px-4 py-3 text-gray-600 text-xs">
                        {formatDate(p.createdAt)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Tablet view */}
            <div className="hidden md:block lg:hidden">
              <div className="grid grid-cols-1 gap-3">
                {patients.map((p, index) => (
                  <div
                    key={p.id}
                    className="border border-gray-200 rounded-lg p-3 hover:border-gray-300 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-2">
                        <div className="text-gray-500 font-medium text-xs min-w-[24px]">
                          {index + 1}.
                        </div>
                        <div className="flex-shrink-0">
                          <div className="w-8 h-8 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-xs font-medium">
                            {getDisplayName(p).charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate max-w-[180px]">
                            {getDisplayName(p)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[180px]">
                            {p.email || "No email"}
                          </div>
                          <div className="text-xs text-gray-700 mt-0.5">
                            {p.contactNo || "No phone"}
                          </div>
                        </div>
                      </div>
                      <Badge
                        className={
                          p.status === "Active"
                            ? "bg-green-50 text-green-700 border-green-200 text-xs"
                            : "bg-gray-50 text-gray-700 border-gray-200 text-xs"
                        }>
                        {p.status || "Inactive"}
                      </Badge>
                    </div>

                    <div className="mt-2 grid grid-cols-3 gap-2 text-xs text-gray-600">
                      <div>
                        <div className="text-gray-500 mb-0.5">Gender</div>
                        <div>{p.gender || "-"}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 mb-0.5">Age</div>
                        <div>{p.age || "-"}</div>
                      </div>
                      <div>
                        <div className="text-gray-500 mb-0.5">Registered</div>
                        <div>{formatDate(p.createdAt)}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Mobile cards - compact design */}
            <div className="md:hidden grid grid-cols-1 gap-2">
              {patients.map((p, index) => (
                <div
                  key={p.id}
                  className="border border-gray-200 rounded-lg p-2 hover:border-gray-300 transition-colors">
                  <div className="flex items-start gap-2">
                    <div className="text-gray-500 font-medium text-xs pt-1 min-w-[20px]">
                      {index + 1}.
                    </div>
                    <div className="flex-1">
                      <div className="flex items-start justify-between">
                        <div className="min-w-0">
                          <div className="font-medium text-gray-900 text-sm truncate max-w-[160px]">
                            {getDisplayName(p)}
                          </div>
                          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                            <span>{p.contactNo || "-"}</span>
                            {p.gender && (
                              <>
                                <span className="text-gray-300">•</span>
                                <span>{p.gender}</span>
                              </>
                            )}
                          </div>
                        </div>
                        <Badge
                          className={
                            (p.status === "Active"
                              ? "bg-green-50 text-green-700 border-green-200"
                              : "bg-gray-50 text-gray-700 border-gray-200") +
                            " text-xs"
                          }>
                          {p.status || "Inactive"}
                        </Badge>
                      </div>

                      <div className="mt-1 text-xs text-gray-600 grid grid-cols-2 gap-1">
                        <div className="truncate">
                          <span className="text-gray-500">Email:</span>{" "}
                          {p.email || "-"}
                        </div>
                        <div>
                          <span className="text-gray-500">Age:</span>{" "}
                          {p.age || "-"}
                        </div>
                        <div className="col-span-2">
                          <span className="text-gray-500">Reg:</span>{" "}
                          {formatDate(p.createdAt)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
